import {
  type ChangeEvent,
  type ElementType,
  type FC,
  type KeyboardEvent,
  type MouseEvent,
  type PointerEvent,
  type ReactNode,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

import {
  AgentsIcon,
  Box,
  ChakraVisuallyHidden,
  CloseIcon,
  IconButton,
  MotionBox,
  RefreshIcon,
  Row,
  Skeleton,
  SkeletonBox,
  Spinner,
  Text,
  TextWithTooltip,
  UserIcon,
  motionDurations,
  motionEasings,
} from "@hightouch/htui";
import {
  type ActionImpl,
  KBarPortal,
  KBarProvider,
  VisualState,
  useKBar,
  useMatches,
} from "kbar";
import { useReducedMotion } from "motion/react";
import { useDebounce } from "use-debounce";

import {
  COMMAND_PALETTE_ATTRIBUTE,
  CommandBarProvider,
  useCommandBar,
  useCommandBarInitialScope,
  useCommandBarPageContext,
} from "src/components/commandbar/context";
import { KeyHints } from "src/components/commandbar/kbd";
import { CommandIndexSource } from "src/components/commandbar/command-index-source";
import { disambiguate } from "src/components/commandbar/disambiguate";
import { takeInitialScope } from "src/components/commandbar/initial-scope";
import {
  type ListPhase,
  isLoadArrival,
  nextListPhase,
} from "src/components/commandbar/list-phase";
import { RecentResultsSource } from "src/components/commandbar/recent-results";
import { CommandResultsContext } from "src/components/commandbar/results-context";
import {
  type PaletteRow,
  buildRows,
  isSelectableRow,
  pickDefaultRow,
  resultKey,
  resultNavigateOptions,
} from "src/components/commandbar/rows";
import {
  type CommandResultSource,
  type CommandResultType,
  type CommandScope,
  type CommandWorkspaceDetails,
  commandScopeLabel,
  commandScopePlaceholder,
} from "src/components/commandbar/types";
import {
  AGENT_CHATS_PATH,
  useShowAgentsInNav,
} from "src/components/layout/nav/sections/hooks/agents";
import { useStartChatFromPrompt } from "src/components/llm/markdown/cards/use-start-chat-from-prompt";
import { useAgentsEnabledFlag } from "src/pages/agents/hooks/use-agents-enabled-flag";
import type { MatchRange } from "src/components/commandbar/match";
import { useConceptOriginForLink } from "src/pages/ad-studio/hooks/use-concept-origin";
import { RouterLink, useNavigate } from "src/router";
import { hasPlatformModifier, isMac } from "src/utils/platform";
import { abbreviateNumber } from "src/utils/numbers";

const LISTBOX_ID = "command-bar-listbox";
const WHEEL_LINE_PX = 16;
const ASK_AGENT_LABEL = "Ask agent";
const TAB_SEARCH_LABEL = "Search";
const ASK_AGENT_KEYS = [isMac ? "⌘" : "Ctrl", "↵"];
const ASK_AGENT_ARIA_KEYS = `${isMac ? "Meta" : "Control"}+Enter`;
const ERROR_LABEL = "Couldn't load results";
// IMEs report a composition-committing Enter as keyCode 229, and Safari clears isComposing before it.
const IME_KEY_CODE = 229;
const ENTER_MS = motionDurations.fast * 1000;
const EXIT_MS = motionDurations.faster * 1000;
const LIVE_REGION_DELAY_MS = 400;
const OPEN = {
  opacity: 1,
  scale: 1,
  transition: {
    duration: motionDurations.fast,
    ease: motionEasings.easeOutQuart,
  },
};
const CLOSED = {
  opacity: 0,
  transition: { duration: motionDurations.faster, ease: motionEasings.linear },
};
const PALETTE_TOP = "clamp(56px, 13vh, 132px)";
// Eight 36px rows plus list padding, with a ninth row cut off so the scroll reads.
const LIST_MAX_HEIGHT_PX = 314;
// Two rows, so a very short viewport still scrolls a list instead of collapsing it.
const LIST_MIN_HEIGHT_PX = 80;
const INPUT_ROW_PX = 52;
const CONTEXT_ROW_PX = 34;
const PINNED_ROW_PX = 45;
const VIEWPORT_GUTTER_PX = 16;
// Loads faster than this never show the skeleton.
const SKELETON_DELAY_MS = 400;
const SKELETON_NAME_WIDTHS = ["40%", "28%", "34%"];
const LIST_REVEAL = {
  duration: motionDurations.fast * 1000,
  easing: `cubic-bezier(${motionEasings.easeOutQuart.join(",")})`,
};

type Row = PaletteRow<ActionImpl>;
type SelectableRow = Extract<
  Row,
  { kind: "action" | "result" | "scope" | "agent" | "error" }
>;
// A collapsed list waits this long before "No results", since a source can report loading a render late.
const EMPTY_DELAY_MS = 100;

const optionId = (key: string) =>
  `command-bar-option-${key.replace(/[^\w-]/g, "_")}`;

// kbar types icons as nodes, but every registered icon is a component.
export const CommandBar: FC<{ children: ReactNode }> = ({ children }) => (
  <CommandBarProvider>
    <KBarProvider
      actions={[]}
      options={{
        animations: { enterMs: ENTER_MS, exitMs: EXIT_MS },
        // kbar's lock hides the document scrollbar, which shifts the page; with no scrim, the page behind stays scrollable.
        disableDocumentLock: true,
      }}
    >
      <FocusReturn />
      <KBarPortal>
        <Palette />
      </KBarPortal>
      {children}
    </KBarProvider>
  </CommandBarProvider>
);

// The control that opened the popover or menu holding `element`, which outlives it when it closes.
const findPopupTrigger = (element: HTMLElement): HTMLElement | null => {
  for (
    let node: HTMLElement | null = element;
    node;
    node = node.parentElement
  ) {
    const trigger = node.id
      ? document.querySelector(`[aria-controls~="${CSS.escape(node.id)}"]`)
      : null;
    if (trigger instanceof HTMLElement && !node.contains(trigger)) {
      return trigger;
    }
  }
  return null;
};

// kbar's own restore can record the palette input as the opener, which leaves focus on body.
const FocusReturn: FC = () => {
  const { query, visualState } = useKBar((state) => ({
    visualState: state.visualState,
  }));
  const opener = useRef<HTMLElement | null>(null);
  const fallback = useRef<HTMLElement | null>(null);

  // A layout effect runs before the palette input takes focus in its passive effect.
  useLayoutEffect(() => {
    if (visualState === VisualState.animatingIn) {
      const active = document.activeElement;
      // Reopening while the palette fades out finds focus still inside it; the first opener stands.
      if (
        active instanceof HTMLElement &&
        !active.closest(`[${COMMAND_PALETTE_ATTRIBUTE}]`)
      ) {
        opener.current = active;
        fallback.current = findPopupTrigger(active);
      }
    } else if (visualState === VisualState.hidden) {
      // Reset once fully closed, not on open: a shortcut that opens a nested page (S then W) keeps it, and a reopen paints no stale matches.
      query.setCurrentRootAction(null);
      query.setSearch("");
      // A closed popover can stay mounted but hidden, so its opener refuses focus and the trigger takes it.
      for (const element of [opener.current, fallback.current]) {
        element?.focus({ preventScroll: true });
        if (element && document.activeElement === element) {
          break;
        }
      }
      opener.current = null;
      fallback.current = null;
    }
  }, [visualState, query]);

  return null;
};

// aria-keyshortcuts names for the key hints a row shows.
const ariaKeyShortcuts = (row: SelectableRow): string | undefined => {
  if (row.kind === "agent") {
    return ASK_AGENT_ARIA_KEYS;
  }
  if (row.kind === "scope" || (row.kind === "action" && row.scope)) {
    return "Tab";
  }
  const shortcut = row.kind === "action" ? row.action.shortcut : undefined;
  return shortcut?.length === 1 ? shortcut[0]?.toUpperCase() : undefined;
};

const Palette: FC = () => {
  const navigate = useNavigate();
  const captureOrigin = useConceptOriginForLink();
  const { isFetching } = useCommandBar();
  const pageContext = useCommandBarPageContext();
  const { query, rootActionId, actions, isClosing, isOpening } = useKBar(
    (state) => ({
      rootActionId: state.currentRootActionId,
      actions: state.actions,
      isClosing: state.visualState === VisualState.animatingOut,
      isOpening: state.visualState === VisualState.animatingIn,
    }),
  );
  const { results: matches } = useMatches();
  // The input reads local state because kbar's store notifies late enough to drop fast keystrokes.
  const [search, setSearchValue] = useState("");
  const initialScope = useCommandBarInitialScope();
  // Peeks without clearing, so a scoped open has no unscoped first frame; the open effect below takes it.
  const [scope, setScope] = useState<CommandScope | null>(
    () => initialScope.current,
  );
  // The scope this open took, kept so a repeat run of the open effect doesn't drop it.
  const openScope = useRef<CommandScope | null | undefined>(undefined);
  // The scope a control opened the palette in, where Escape closes rather than leaves it.
  const launchScope = useRef<CommandScope | null>(null);
  // An Enter pressed before any row could take it, for the query and scope it was pressed on.
  const pendingEnter = useRef<{
    search: string;
    scope: CommandScope | null;
  } | null>(null);
  const [sources, setSources] = useState(
    () => new Map<string, CommandResultSource>(),
  );
  // `byUser` keeps a picked row highlighted while async results arrive; otherwise the best match leads.
  const [active, setActive] = useState<{ key: string | null; byUser: boolean }>(
    { key: null, byUser: false },
  );
  const pointer = useRef({ x: -1, y: -1 });
  const scrollToActive = useRef(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const resultsScrollRef = useRef<HTMLDivElement | null>(null);

  // A page modal's scroll lock cancels wheels outside it, so the list scrolls itself when one was cancelled.
  // Window listeners run after the lock's document listener, so the cancel is visible here.
  useEffect(() => {
    const onWheel = (event: WheelEvent) => {
      const listbox = resultsScrollRef.current;
      if (
        !event.defaultPrevented ||
        !listbox ||
        !(event.target instanceof Node) ||
        !listbox.contains(event.target)
      ) {
        return;
      }
      const unit =
        event.deltaMode === WheelEvent.DOM_DELTA_LINE
          ? WHEEL_LINE_PX
          : event.deltaMode === WheelEvent.DOM_DELTA_PAGE
            ? listbox.clientHeight
            : 1;
      listbox.scrollTop += event.deltaY * unit;
    };
    window.addEventListener("wheel", onWheel);
    return () => window.removeEventListener("wheel", onWheel);
  }, []);

  const sourceList = [...sources.values()];
  const canViewAgents = useShowAgentsInNav();
  const isAgentsEnabled = useAgentsEnabledFlag();
  const startChatFromPrompt = useStartChatFromPrompt();
  // Nested command pages have nothing to ask about; type scopes still pass the query on.
  const canAskAgent = canViewAgents && isAgentsEnabled && !rootActionId;
  const rows = buildRows({
    canAskAgent,
    search,
    scope,
    rootActionId,
    matches,
    allActions: Object.values(actions),
    contextActionIds: pageContext?.actions.map((action) => action.id) ?? [],
    contextObjectKey: pageContext ? resultKey(pageContext.object) : null,
    sources: sourceList,
  });
  const selectable = rows.filter(isSelectableRow);
  const sourceScopes = sourceList.flatMap((source) => source.scopes ?? []);
  const scopeFor = (type: CommandResultType) =>
    sourceScopes.find((candidate) => candidate.type === type);
  const details = disambiguate(
    selectable.flatMap((row) => (row.kind === "result" ? [row.result] : [])),
    (type) => commandScopeLabel(scopeFor(type) ?? { type }),
  );
  const isLoading = isFetching || sourceList.some((source) => source.isLoading);
  const matched = selectable.filter((row) => row.kind !== "agent");
  const agentRow = selectable.find((row) => row.kind === "agent");
  const errorRow = selectable.find((row) => row.kind === "error");
  // For one typed query the first default holds while it's listed, so a scope or result that arrives late never takes Enter from it.
  // With nothing typed, recents land above the commands and the first row leads.
  const heldDefault = useRef<{ search: string; key: string } | null>(null);
  const held =
    search.trim() &&
    heldDefault.current?.search === search &&
    selectable.some((row) => row.key === heldDefault.current?.key)
      ? heldDefault.current.key
      : null;
  const defaultRow = held ? undefined : pickDefaultRow(selectable, isLoading);
  const defaultKey = held ?? defaultRow?.key ?? null;
  // Ask agent never holds: sources report loading a render after the query changes, so it can win before results land.
  heldDefault.current =
    defaultKey && defaultRow?.kind !== "agent"
      ? { search, key: defaultKey }
      : null;
  const activeKey =
    active.byUser && selectable.some((row) => row.key === active.key)
      ? active.key
      : defaultKey;
  const activeRow = selectable.find((row) => row.key === activeKey);
  const contextIcons = new Map(
    pageContext?.actions.map((action) => [action.id, action.icon]),
  );
  const rootAction = rootActionId ? actions[rootActionId] : undefined;
  const chip = scope
    ? { icon: scope.icon, label: commandScopeLabel(scope) }
    : rootAction
      ? { icon: rootAction.iconComponent, label: rootAction.name }
      : null;

  const inputLabel = scope
    ? commandScopePlaceholder(scope)
    : rootAction
      ? `Search ${rootAction.name}`
      : "Search or run a command";

  const lastListPhase = useRef<ListPhase>("collapsed");
  // The query a load started for; a load counts as arriving only while the query is unchanged.
  const pendingLoadQuery = useRef<string | null>(null);
  const isWaitingForRows = isLoading && !matched.length;
  const isEmpty =
    !selectable.length && !isLoading && Boolean(search.trim() || scope);
  const isSlowLoad = useHasLasted(isWaitingForRows, SKELETON_DELAY_MS);
  const isSettledEmpty = useHasLasted(isEmpty, EMPTY_DELAY_MS);
  const lastPhase = lastListPhase.current;
  const listPhase = nextListPhase({
    lastPhase,
    hasRows: matched.length > 0 || (Boolean(agentRow) && !isLoading),
    isWaitingForRows,
    isEmpty,
    isSlowLoad,
    isSettledEmpty,
  });
  const shouldAnimateList = isLoadArrival({
    phase: listPhase,
    lastPhase,
    isLoadPending: pendingLoadQuery.current === search,
  });
  useLayoutEffect(() => {
    lastListPhase.current = listPhase;
    if (isWaitingForRows) {
      pendingLoadQuery.current = search;
    } else if (listPhase === "rows" || listPhase === "empty") {
      pendingLoadQuery.current = null;
    }
  });

  // At high zoom the list shrinks so the pinned agent row stays on screen.
  const chromePx =
    INPUT_ROW_PX +
    VIEWPORT_GUTTER_PX +
    1 +
    (pageContext && !rootActionId ? CONTEXT_ROW_PX : 0) +
    (agentRow ? PINNED_ROW_PX : 0);
  const listMaxHeight = `max(${LIST_MIN_HEIGHT_PX}px, min(${LIST_MAX_HEIGHT_PX}px, calc(100vh - ${PALETTE_TOP} - ${chromePx}px)))`;

  const hasNoMatches =
    listPhase === "empty" || (listPhase === "rows" && !matched.length);
  const noResultsLabel = search.trim()
    ? `No results for “${search.trim()}”`
    : "No results";

  const resultCount = selectable.filter(
    (row) => row.kind === "action" || row.kind === "result",
  ).length;
  const [liveMessage] = useDebounce(
    isLoading
      ? ""
      : resultCount
        ? `${resultCount} ${resultCount === 1 ? "result" : "results"}`
        : errorRow
          ? ERROR_LABEL
          : "No results",
    LIVE_REGION_DELAY_MS,
  );

  useEffect(() => {
    if (!scrollToActive.current || !activeKey) {
      return;
    }
    scrollToActive.current = false;
    document
      .getElementById(optionId(activeKey))
      ?.scrollIntoView({ block: "nearest" });
  }, [activeKey]);

  const hasAskedAgent = useRef(false);

  // Every open starts empty, including a reopen during the fade-out, which keeps this palette mounted.
  useEffect(() => {
    if (!isOpening) {
      openScope.current = undefined;
      return;
    }
    setSearchValue("");
    // A control can open the palette scoped to a type; only that open starts scoped.
    if (openScope.current === undefined) {
      openScope.current = takeInitialScope(initialScope);
    }
    launchScope.current = openScope.current;
    pendingEnter.current = null;
    setScope(openScope.current);
    setActive({ key: null, byUser: false });
    hasAskedAgent.current = false;
    query.setSearch("");
    // Focusing here rather than with autoFocus lets FocusReturn record the opener first.
    inputRef.current?.focus();
  }, [isOpening, query, initialScope]);

  const resetActive = () => setActive({ key: null, byUser: false });

  const updateSearch = (next: string) => {
    setSearchValue(next);
    pendingEnter.current = null;
    // Only a nested page's rows come from kbar's matches; the root ranks every action itself, so kbar skips the fuzzy pass there.
    query.setSearch(rootActionId && next.trim() ? next : "");
    resetActive();
  };

  const setRoot = (actionId: string | null | undefined) => {
    updateSearch("");
    query.setCurrentRootAction(actionId);
  };

  const enterScope = (next: CommandScope) => {
    setScope(next);
    updateSearch("");
  };

  const askAgent = () => {
    if (hasAskedAgent.current) {
      return;
    }
    hasAskedAgent.current = true;
    const prompt = search.trim();
    query.toggle();
    if (prompt) {
      void startChatFromPrompt(prompt);
    } else {
      navigate(AGENT_CHATS_PATH);
    }
  };

  const retryFailedSources = () => {
    for (const source of sourceList) {
      if (source.isError) {
        source.onRetry?.();
      }
    }
  };

  const execute = (row: SelectableRow) => {
    if (row.kind === "error") {
      retryFailedSources();
      return;
    }
    if (row.kind === "agent") {
      askAgent();
      return;
    }
    if (row.kind === "scope") {
      // Enter opens the type's page; Tab searches inside it.
      if (row.scope.href) {
        navigate(row.scope.href);
        query.toggle();
      } else {
        enterScope(row.scope);
      }
      return;
    }
    if (row.kind === "result") {
      navigate(
        row.result.href,
        resultNavigateOptions(row.result, captureOrigin),
      );
      query.toggle();
      return;
    }
    const { action } = row;
    if (action.command) {
      action.command.perform(action);
      // An action can open a nested page itself ("Switch workspace"), which starts empty.
      updateSearch("");
      query.toggle();
    } else {
      setRoot(action.id);
    }
  };

  // An early Enter waits for a real row to lead, never Ask agent or a retry; closing or changing the query or scope drops it.
  useEffect(() => {
    const pending = pendingEnter.current;
    if (!pending) {
      return;
    }
    if (isClosing || pending.search !== search || pending.scope !== scope) {
      pendingEnter.current = null;
      return;
    }
    if (activeRow && activeRow.kind !== "agent" && activeRow.kind !== "error") {
      pendingEnter.current = null;
      execute(activeRow);
    }
  });

  const move = (delta: number) => {
    const index = selectable.findIndex((row) => row.key === activeKey);
    // With no active row, ArrowDown starts at the first row and ArrowUp at the last.
    const from = index === -1 ? (delta > 0 ? -1 : 0) : index;
    const next =
      selectable[(from + delta + selectable.length) % selectable.length];
    if (next) {
      scrollToActive.current = true;
      setActive({ key: next.key, byUser: true });
    }
  };

  const popScope = () => {
    if (scope) {
      setScope(null);
      resetActive();
      return true;
    }
    if (rootActionId) {
      setRoot(actions[rootActionId]?.parent);
      return true;
    }
    return false;
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.nativeEvent.isComposing || event.keyCode === IME_KEY_CODE) {
      return;
    }
    const isCtrlOnly = event.ctrlKey && !event.metaKey;
    if (event.key === "ArrowDown" || (isCtrlOnly && event.key === "n")) {
      event.preventDefault();
      move(1);
    } else if (event.key === "ArrowUp" || (isCtrlOnly && event.key === "p")) {
      event.preventDefault();
      move(-1);
    } else if (event.key === "Enter" && hasPlatformModifier(event)) {
      // A modified Enter only ever asks the agent, never runs the highlighted row.
      event.preventDefault();
      if (canAskAgent) {
        askAgent();
      }
    } else if (
      event.key === "Escape" &&
      scope &&
      scope !== launchScope.current
    ) {
      // Escape leaves a type scope first; kbar's window listener closes the palette on the next one.
      event.preventDefault();
      event.stopPropagation();
      setScope(null);
      resetActive();
    } else if (event.key === "Enter") {
      event.preventDefault();
      if (activeRow) {
        execute(activeRow);
      } else if (isLoading) {
        pendingEnter.current = { search, scope };
      }
    } else if (event.key === "Tab") {
      event.preventDefault();
      if (event.shiftKey) {
        popScope();
      } else if (activeRow?.kind === "scope") {
        enterScope(activeRow.scope);
      } else if (activeRow?.kind === "action" && activeRow.scope) {
        enterScope(activeRow.scope);
      } else if (activeRow?.kind === "result" && !scope) {
        const { type } = activeRow.result;
        // Only a type a source searches has a scope; the one the palette opened in keeps its brand filter.
        const next =
          launchScope.current?.type === type
            ? launchScope.current
            : scopeFor(type);
        if (next) {
          setScope(next);
          resetActive();
        }
      } else if (
        activeRow?.kind === "action" &&
        activeRow.action.children.length
      ) {
        setRoot(activeRow.action.id);
      }
    } else if (event.key === "Backspace" && !search && popScope()) {
      event.preventDefault();
    }
  };

  const onRowPointerMove = (event: PointerEvent, key: string) => {
    if (
      event.clientX === pointer.current.x &&
      event.clientY === pointer.current.y
    ) {
      return;
    }
    pointer.current = { x: event.clientX, y: event.clientY };
    if (key !== activeKey) {
      setActive({ key, byUser: true });
    }
  };

  const renderOption = (row: SelectableRow) => (
    <OptionRow
      key={row.key}
      row={row}
      isActive={row.key === activeKey}
      // A typed root query ranks nested actions too, so name the pages they sit under, past the open page or scope.
      ancestorNames={
        row.kind === "action"
          ? row.action.ancestors
              .slice(
                row.action.ancestors.findIndex(
                  (ancestor) => ancestor.id === (rootActionId ?? scope?.type),
                ) + 1,
              )
              .map((ancestor) => ancestor.name)
          : []
      }
      href={
        row.kind === "result"
          ? row.result.href
          : row.kind === "scope" || row.kind === "action"
            ? row.scope?.href
            : undefined
      }
      detail={
        row.kind === "result" ? details.get(resultKey(row.result)) : undefined
      }
      icon={
        row.kind === "result"
          ? row.result.icon
          : row.kind === "scope"
            ? row.scope.icon
            : row.kind === "agent"
              ? AgentsIcon
              : row.kind === "error"
                ? RefreshIcon
                : (contextIcons.get(row.action.id) ??
                  [row.action, ...row.action.ancestors]
                    .map((action) => action.iconComponent)
                    .find(Boolean))
      }
      onPointerMove={(event) => onRowPointerMove(event, row.key)}
      onClick={() => execute(row)}
    />
  );

  return (
    <CommandResultsContext.Provider value={{ search, scope, setSources }}>
      <RecentResultsSource />
      <CommandIndexSource />
      <Box
        position="fixed"
        inset={0}
        zIndex="popover"
        display="flex"
        justifyContent="center"
        alignItems="flex-start"
        px={4}
        pt={PALETTE_TOP}
        // A click during the fade-out lands on the page, not on the fading palette.
        pointerEvents={isClosing ? "none" : undefined}
        // A page modal's focus trap would pull focus back out of the palette, leaving keys and Escape with the modal.
        data-no-focus-lock
        onPointerDown={(event) => {
          if (event.target === event.currentTarget) {
            query.setVisualState(VisualState.animatingOut);
          }
        }}
      >
        <MotionBox
          role="dialog"
          aria-modal="true"
          {...{ [COMMAND_PALETTE_ATTRIBUTE]: "" }}
          aria-label="Command menu"
          // Focus stays in the input: with a page modal open, focus on the body would be pulled back into the modal.
          onPointerDown={(event: PointerEvent) => {
            // A middle click keeps its default, so a row's link can still open in a new tab.
            if (event.button === 0 && event.target !== inputRef.current) {
              event.preventDefault();
            }
          }}
          width="100%"
          maxWidth="640px"
          bg="white"
          borderRadius="2xl"
          boxShadow="lg"
          overflow="hidden"
          // Keeps a wheel or swipe over the palette from scrolling the page behind it.
          overscrollBehavior="contain"
          transformOrigin="50% 0"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={isClosing ? CLOSED : OPEN}
        >
          {pageContext && !rootActionId && (
            <Row px={3} pt={2.5}>
              <Chip
                icon={pageContext.object.icon}
                label={pageContext.object.name}
              />
            </Row>
          )}
          <Row align="center" gap={2} height="52px" px={4}>
            {chip && (
              <Chip icon={chip.icon} label={chip.label} onRemove={popScope} />
            )}
            <Box
              as="input"
              ref={(element: HTMLInputElement | null) => {
                inputRef.current = element;
                if (element) {
                  query.inputRefSetter(element);
                }
              }}
              role="combobox"
              aria-label={inputLabel}
              aria-expanded={listPhase !== "collapsed"}
              aria-controls={LISTBOX_ID}
              aria-autocomplete="list"
              aria-activedescendant={
                activeKey ? optionId(activeKey) : undefined
              }
              autoComplete="off"
              spellCheck={false}
              placeholder={inputLabel}
              value={search}
              onChange={(event: ChangeEvent<HTMLDivElement>) =>
                updateSearch((event.target as HTMLInputElement).value)
              }
              onKeyDown={onKeyDown}
              flex={1}
              minWidth={0}
              height="100%"
              bg="transparent"
              border="none"
              outline="none"
              color="text.primary"
              fontSize="lg"
              _placeholder={{ color: "text.disabled" }}
            />
            {isLoading ? (
              <Spinner size="sm" />
            ) : canAskAgent && !search.trim() ? (
              <Row as="span" align="center" gap={2} flexShrink={0}>
                <Text size="sm" color="text.tertiary">
                  {ASK_AGENT_LABEL}
                </Text>
                <KeyHints keys={ASK_AGENT_KEYS} isActive={false} />
              </Row>
            ) : null}
          </Row>
          <ListReveal phase={listPhase} shouldAnimate={shouldAnimateList}>
            <Box
              id={LISTBOX_ID}
              role="listbox"
              aria-label="Results"
              aria-busy={isLoading}
              {...(listPhase !== "collapsed" && {
                borderTopWidth: "hairline",
                borderColor: "base.divider",
              })}
            >
              {listPhase !== "collapsed" && (
                <Box
                  ref={resultsScrollRef}
                  role="presentation"
                  p={1}
                  maxHeight={listMaxHeight}
                  overflowY="auto"
                  overscrollBehavior="contain"
                >
                  {listPhase === "rows" &&
                    rows.map((row) =>
                      row.kind === "agent" ? null : isSelectableRow(row) ? (
                        renderOption(row)
                      ) : row.kind === "header" ? (
                        <Box
                          key={row.key}
                          role="presentation"
                          px={2.5}
                          pt={2}
                          pb={1}
                        >
                          <Text size="sm" color="text.tertiary">
                            {row.label}
                          </Text>
                        </Box>
                      ) : (
                        <Box
                          key={row.key}
                          role="presentation"
                          borderTopWidth="hairline"
                          borderColor="base.divider"
                          mx={1.5}
                          my={1}
                        />
                      ),
                    )}
                  {listPhase === "skeleton" && <SkeletonRows />}
                  {hasNoMatches && (
                    <Row
                      role="presentation"
                      align="center"
                      height={9}
                      px={2.5}
                      minWidth={0}
                    >
                      <TextWithTooltip color="text.tertiary">
                        {noResultsLabel}
                      </TextWithTooltip>
                    </Row>
                  )}
                </Box>
              )}
              {/* Pinned below the scrolling results so asking the agent stays in view however long the list runs. */}
              {agentRow && listPhase !== "collapsed" && (
                <Box
                  role="presentation"
                  p={1}
                  borderTopWidth="hairline"
                  borderColor="base.divider"
                >
                  {renderOption(agentRow)}
                </Box>
              )}
            </Box>
          </ListReveal>
          <ChakraVisuallyHidden role="status" aria-live="polite">
            {liveMessage}
          </ChakraVisuallyHidden>
        </MotionBox>
      </Box>
    </CommandResultsContext.Provider>
  );
};

type ChipProps = {
  icon: ElementType | undefined;
  label: string;
  onRemove?: () => void;
};

const Chip: FC<ChipProps> = ({ icon, label, onRemove }) => (
  <Row
    as="span"
    align="center"
    gap={1.5}
    flexShrink={0}
    maxWidth="50%"
    height={6}
    pl={1.5}
    pr={onRemove ? 0.5 : 2}
    borderRadius="md"
    bg="base.background"
    color="text.secondary"
    fontSize="sm"
    fontWeight="medium"
    whiteSpace="nowrap"
  >
    {icon && (
      <Box as={icon} boxSize={3.5} flexShrink={0} color="text.tertiary" />
    )}
    <Box minWidth={0}>
      <TextWithTooltip size="sm" fontWeight="medium" color="text.secondary">
        {label}
      </TextWithTooltip>
    </Box>
    {onRemove && (
      <IconButton
        aria-label={`Remove ${label}`}
        icon={CloseIcon}
        size="2xs"
        variant="tertiary"
        // Keyboard users leave a scope with Backspace or Shift+Tab, so focus stays in the input.
        tabIndex={-1}
        onClick={onRemove}
      />
    )}
  </Row>
);

type OptionRowProps = {
  row: SelectableRow;
  isActive: boolean;
  ancestorNames: string[];
  icon: ElementType | undefined;
  // Rows that open a page are real links, so a modified or middle click opens it in a new tab.
  href?: string;
  detail?: string;
  onPointerMove: (event: PointerEvent) => void;
  onClick: () => void;
};

// RouterLink leaves these to the browser, which opens a new tab or window.
const isNewTabClick = (event: MouseEvent) =>
  event.metaKey || event.ctrlKey || event.shiftKey;

const highlight = (name: string, ranges: MatchRange[]) => {
  const parts: ReactNode[] = [];
  let cursor = 0;
  for (const { start, end } of ranges) {
    parts.push(name.slice(cursor, start));
    parts.push(
      <Box as="b" key={start} fontWeight="semibold">
        {name.slice(start, end)}
      </Box>,
    );
    cursor = end;
  }
  parts.push(name.slice(cursor));
  return parts;
};

const OptionRow: FC<OptionRowProps> = ({
  row,
  isActive,
  ancestorNames,
  icon,
  href,
  detail,
  onPointerMove,
  onClick,
}) => {
  const name =
    row.kind === "agent"
      ? `${ASK_AGENT_LABEL} “${row.query}”`
      : row.kind === "error"
        ? ERROR_LABEL
        : row.kind === "scope"
          ? row.label
          : row.kind === "result"
            ? row.result.name
            : row.action.name;
  const isSearchable =
    row.kind === "scope" || (row.kind === "action" && Boolean(row.scope));
  const shortcut =
    row.kind === "agent"
      ? ASK_AGENT_KEYS
      : row.kind === "scope" || (row.kind === "action" && row.scope)
        ? ["Tab"]
        : row.kind === "action"
          ? row.action.shortcut
          : undefined;
  const trailingText = (row.kind === "action" && row.action.subtitle) || detail;
  const workspace = row.kind === "action" ? row.action.workspace : undefined;

  return (
    <Row
      {...(href && {
        as: RouterLink,
        to: href,
        tabIndex: -1,
        draggable: false,
        color: "inherit",
        _hover: { textDecoration: "none" },
      })}
      id={optionId(row.key)}
      role="option"
      aria-selected={isActive}
      aria-keyshortcuts={ariaKeyShortcuts(row)}
      align="center"
      gap={2.5}
      height={9}
      px={2.5}
      borderRadius="lg"
      bg={isActive ? "blackAlpha.04" : undefined}
      userSelect="none"
      onPointerMove={onPointerMove}
      // Keeps focus in the input so the keyboard path never breaks; other buttons keep their defaults, such as a middle click's new tab.
      onPointerDown={(event) => {
        if (event.button === 0) {
          event.preventDefault();
        }
      }}
      onClick={(event: MouseEvent) => {
        if (href && isNewTabClick(event)) {
          return;
        }
        event.preventDefault();
        onClick();
      }}
    >
      {icon && (
        <Box
          as={icon}
          boxSize={4}
          flexShrink={0}
          color={isActive ? "text.primary" : "text.tertiary"}
        />
      )}
      <Box flex={1} minWidth={0}>
        <TextWithTooltip
          message={[...ancestorNames, name].join(" › ")}
          color="text.primary"
        >
          {ancestorNames.map((ancestor) => (
            <Box as="span" key={ancestor} color="text.tertiary">
              {ancestor} ›{" "}
            </Box>
          ))}
          {row.kind === "agent" ? (
            <>
              {ASK_AGENT_LABEL}{" "}
              <Box as="span" color="text.secondary">
                “{row.query}”
              </Box>
            </>
          ) : row.match ? (
            highlight(name, row.match.ranges)
          ) : (
            name
          )}
        </TextWithTooltip>
      </Box>
      {shortcut?.length ? (
        <Row as="span" align="center" gap={2} flexShrink={0}>
          {isSearchable && (
            // Says what Tab does, since Enter on the same row opens the page.
            <Text size="sm" color="text.tertiary" aria-hidden>
              {TAB_SEARCH_LABEL}
            </Text>
          )}
          <KeyHints keys={shortcut} isActive={isActive} />
        </Row>
      ) : workspace ? (
        <WorkspaceDetails details={workspace} organization={trailingText} />
      ) : trailingText ? (
        // The name keeps most of the row; a long detail truncates first.
        <Box flexShrink={1} minWidth={0} maxWidth="40%">
          <TextWithTooltip size="sm" color="text.tertiary">
            {trailingText}
          </TextWithTooltip>
        </Box>
      ) : null}
    </Row>
  );
};

const WorkspaceDetails: FC<{
  details: CommandWorkspaceDetails;
  organization: string | undefined;
}> = ({ details, organization }) => (
  <Row align="center" gap={4} flexShrink={1} minWidth={0} maxWidth="50%">
    {organization && (
      <Box minWidth={0}>
        <TextWithTooltip size="sm" color="text.tertiary">
          {organization}
        </TextWithTooltip>
      </Box>
    )}
    {/* Fixed widths line the count and id columns up across rows; they fit "1.5K" and six digits. */}
    <Row align="center" justify="flex-end" gap={1} width={12} flexShrink={0}>
      {details.memberCount != null && (
        <>
          <Box as={UserIcon} boxSize={3.5} color="text.tertiary" />
          <Text size="sm" color="text.tertiary" variant="numeric">
            {abbreviateNumber(details.memberCount)}
            <ChakraVisuallyHidden> members</ChakraVisuallyHidden>
          </Text>
        </>
      )}
    </Row>
    <Row justify="flex-end" width={12} flexShrink={0}>
      <Text size="sm" color="text.tertiary" variant="numeric">
        {details.id}
      </Text>
    </Row>
  </Row>
);

// True once `active` has held for `ms`, false as soon as it drops.
const useHasLasted = (active: boolean, ms: number) => {
  const [hasLasted, setHasLasted] = useState(false);
  useEffect(() => {
    setHasLasted(false);
    if (!active) {
      return;
    }
    const timer = setTimeout(() => setHasLasted(true), ms);
    return () => clearTimeout(timer);
  }, [active, ms]);
  return active && hasLasted;
};

const SkeletonRows: FC = () => (
  <Skeleton isLoading>
    <Box aria-hidden>
      {SKELETON_NAME_WIDTHS.map((width) => (
        <Row
          key={width}
          data-command-skeleton
          align="center"
          gap={2.5}
          height={9}
          px={2.5}
        >
          <SkeletonBox boxSize={4} flexShrink={0} />
          <SkeletonBox height={2.5} width={width} />
        </Row>
      ))}
    </Box>
  </Skeleton>
);

type ListRevealProps = {
  phase: ListPhase;
  shouldAnimate: boolean;
  children: ReactNode;
};

// Sizes the list to its content, tweening height and opacity only on a phase change the caller marks.
const ListReveal: FC<ListRevealProps> = ({
  phase,
  shouldAnimate,
  children,
}) => {
  const shouldReduceMotion = useReducedMotion() ?? false;
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const lastPhase = useRef(phase);
  const lastHeight = useRef<number | null>(null);

  useLayoutEffect(() => {
    const wrapper = wrapperRef.current;
    const content = contentRef.current;
    if (!wrapper || !content) {
      return;
    }
    const to = content.offsetHeight;
    const isPhaseChange = lastPhase.current !== phase;
    lastPhase.current = phase;
    if (to === lastHeight.current && !isPhaseChange) {
      return;
    }
    // Reading the live height lets an interrupted tween continue from where it is.
    const from = wrapper.getBoundingClientRect().height;
    lastHeight.current = to;
    for (const animation of wrapper.getAnimations()) {
      animation.cancel();
    }
    wrapper.style.height = `${to}px`;
    if (!isPhaseChange || !shouldAnimate) {
      return;
    }
    if (!shouldReduceMotion && from !== to) {
      wrapper.animate(
        [{ height: `${from}px` }, { height: `${to}px` }],
        LIST_REVEAL,
      );
    }
    content.animate([{ opacity: 0 }, { opacity: 1 }], LIST_REVEAL);
  }, [phase, shouldAnimate, shouldReduceMotion]);

  // Height changes within a phase, as rows arrive or the viewport resizes, snap without a tween.
  useLayoutEffect(() => {
    const wrapper = wrapperRef.current;
    const content = contentRef.current;
    if (!wrapper || !content) {
      return;
    }
    const observer = new ResizeObserver(() => {
      const to = content.offsetHeight;
      if (to === lastHeight.current) {
        return;
      }
      lastHeight.current = to;
      for (const animation of wrapper.getAnimations()) {
        animation.cancel();
      }
      wrapper.style.height = `${to}px`;
    });
    observer.observe(content);
    return () => observer.disconnect();
  }, []);

  return (
    <Box ref={wrapperRef} overflow="hidden">
      <Box ref={contentRef}>{children}</Box>
    </Box>
  );
};
